/**
 * Alerta Slack (#juaperrone) cuando un visitor no puede ver el video Mux.
 * Best-effort: dedup client-side + backend dedup; nunca bloquea UX.
 */
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import { detectCountryCode } from "@/lib/countryDetection";
import {
  getSlackFunnelDiagnosticsExtraPostHog,
  isPostHogConfigured,
} from "@/lib/posthogAnalytics";
import posthog from "posthog-js";

export type MuxVideoFailureContext = {
  template: string;
  errorType?: string;
  errorMessage: string;
  playbackId?: string;
  country?: string;
  videoStarted?: boolean;
  videoCompleted?: boolean;
  ctaVipClicked?: boolean;
  ctaFreeClicked?: boolean;
  checkoutOpened?: boolean;
  timeOnPageSeconds?: number;
  dualVariantId?: string;
  dualVariantGeoScope?: string;
  dualVariantGeoCode?: string;
  isNetworkError?: boolean;
  isTokenError?: boolean;
  service?: string;
  /** Mux event name (video_error, playback_id_invalid, …) */
  muxEvent?: string;
  extra?: Record<string, string | number | boolean>;
};

const SLACK_DEDUP_MS = 2 * 60 * 1000;
const STORAGE_PREFIX = "mux_fail_slack_v1:";

function shouldClientDedupe(key: string): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return false;
    const ts = Number(raw);
    return Number.isFinite(ts) && Date.now() - ts < SLACK_DEDUP_MS;
  } catch {
    return false;
  }
}

function markClientDedupe(key: string): void {
  try {
    sessionStorage.setItem(STORAGE_PREFIX + key, String(Date.now()));
  } catch {
    /* ignore */
  }
}

/** Infer template name from pathname when caller doesn't pass one. */
export function inferSlackFunnelTemplateFromPath(pathname?: string): string {
  const p = (pathname ?? (typeof window !== "undefined" ? window.location.pathname : "")).toLowerCase();
  if (p.includes("video-landing") || p.includes("ser-miembro")) return "DualCTAVideoLanding";
  if (p.includes("membership")) return "VideoTemplate";
  if (p.includes("exclusive") || p.includes("sorpresa")) return "BioTemplatePremiumSimple";
  return "MuxVideoPlayerWithCTA";
}

export function trackMuxVideoFailureSlack(ctx: MuxVideoFailureContext): void {
  if (typeof window === "undefined") return;

  const errorType = (ctx.errorType || "unknown").slice(0, 64);
  const playbackId = (ctx.playbackId || "none").slice(0, 48);
  const msgKey = ctx.errorMessage.slice(0, 80);
  const dedupeKey = `${ctx.template}|${errorType}|${playbackId}|${msgKey}`;

  if (shouldClientDedupe(dedupeKey)) return;
  markClientDedupe(dedupeKey);

  const cfg = getServerTemplateConfig();
  const country = (ctx.country ?? cfg?.country ?? detectCountryCode() ?? "UNKNOWN").toUpperCase();
  const phDiag = getSlackFunnelDiagnosticsExtraPostHog({ replayNearNow: true });

  const connectionType = (() => {
    try {
      return (
        (navigator as { connection?: { effectiveType?: string } }).connection
          ?.effectiveType ?? "unknown"
      );
    } catch {
      return "unknown";
    }
  })();

  const mergedExtra: Record<string, string | number | boolean> = {
    ...phDiag,
    error_type: errorType,
    playback_id: playbackId,
    connection_type: connectionType,
    video_started: Boolean(ctx.videoStarted),
    video_completed: Boolean(ctx.videoCompleted),
    cta_vip_clicked: Boolean(ctx.ctaVipClicked),
    cta_free_clicked: Boolean(ctx.ctaFreeClicked),
    checkout_opened: Boolean(ctx.checkoutOpened),
    is_network_error: Boolean(ctx.isNetworkError),
    is_token_error: Boolean(ctx.isTokenError),
    conversion_blocker: "video_unavailable",
    worker_city: cfg?.city ?? "",
    worker_language: cfg?.language ?? "",
    worker_template: cfg?.templateName ?? "",
    ...(ctx.service ? { mux_service: ctx.service } : {}),
    ...(ctx.muxEvent ? { mux_event: ctx.muxEvent } : {}),
    ...(ctx.dualVariantId
      ? {
          dual_variant_id: ctx.dualVariantId,
          dual_variant_geo_scope: ctx.dualVariantGeoScope ?? "",
          dual_variant_geo_code: ctx.dualVariantGeoCode ?? "",
        }
      : {}),
    ...(ctx.extra ?? {}),
  };

  if (isPostHogConfigured()) {
    posthog.capture("dual_cta_video_error", {
      template: ctx.template,
      error_type: errorType,
      error_message: ctx.errorMessage.slice(0, 500),
      playback_id: playbackId,
      mux_event: ctx.muxEvent,
      video_started: ctx.videoStarted,
      video_completed: ctx.videoCompleted,
      cta_vip_clicked: ctx.ctaVipClicked,
      conversion_blocker: "video_unavailable",
      country,
      ...mergedExtra,
    });
  }

  try {
    void fetch("/api/track-funnel-failure", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      keepalive: true,
      body: JSON.stringify({
        kind: "mux_playback_error",
        template: ctx.template,
        country,
        error_message: ctx.errorMessage.slice(0, 800),
        time_on_page_seconds: ctx.timeOnPageSeconds,
        page_url: window.location.href,
        extra: mergedExtra,
      }),
    }).catch(() => {});
  } catch {
    /* best-effort */
  }
}
